import React, { useEffect, useState } from 'react';
import statementService from '../../../services/statementService';
import { useT } from '../../../i18n/LanguageContext';
import { money, stampLabel } from '../formatters';
import { errorText, unwrap } from '../responseSafety';
import { refusalLabel, statementOutcomeLabel } from '../runPresentation';
import Banner from './Banner';
import Modal from './Modal';
import Skeleton from './Skeleton';

/**
 * Whether a run actually emailed anybody.
 *
 * A rehearsal and a live run are the same row SHAPE but not the same counts: a rehearsal records
 * every customer as `simulated` and leaves `sent` at zero. So the badge is the only thing on the
 * row that says whether anyone was contacted, and the count column has to be read through it -
 * which is why the column is headed "Statements" rather than "Emailed". An aborted run is called
 * out separately because its counts are all zero and would otherwise read as a quiet week.
 */
export function RunModeBadge({ run }) {
  const t = useT();
  const live = run?.mode === 'live';
  if (run?.abortedReason) {
    return (
      <span className="text-[11px] px-2 py-0.5 rounded-full bg-[rgba(254,226,226,0.6)] text-[#f23e41]">
        {t('Stopped')}
      </span>
    );
  }
  return (
    <span
      className={`text-[11px] px-2 py-0.5 rounded-full ${
        live ? 'bg-[#dcfce7] text-[#15803d]' : 'bg-[#f1f5f9] text-[#475569]'
      }`}
    >
      {live ? t('Emailed') : t('Rehearsal')}
    </span>
  );
}

/** What one past run decided, customer by customer. */
export default function RunDetail({ runId, onClose }) {
  const t = useT();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await statementService.getRun(runId);
        if (live) setData(unwrap(res, 'a run'));
      } catch (e) {
        if (live) setError(errorText(e, 'Could not load that run.'));
      }
    })();
    return () => {
      live = false;
    };
  }, [runId]);

  const run = data?.run;

  return (
    <Modal
      labelledBy="run-detail-title"
      onClose={onClose}
      className="max-w-3xl max-h-[90vh] flex flex-col"
    >
      <>
        <div className="px-6 py-4 border-b border-[#e2e8f0] flex items-center justify-between">
          <div>
            <h2 id="run-detail-title" className="text-[18px] font-bold text-[#0f172a]">
              {t('Run detail')}
            </h2>
            <p className="text-[13px] text-[#64748b]">
              {run
                ? t('{stamp} - week of {weekOf}', {
                    stamp: stampLabel(run.createdAt),
                    weekOf: run.weekOf,
                  })
                : runId}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-[#64748b] hover:text-[#0f172a]"
            aria-label={t('Close')}
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto">
          {error && <Banner tone="error">{t(error)}</Banner>}
          {!data && !error && <Skeleton rows={5} />}

          {run?.abortedReason && (
            <Banner tone="error">
              {t('This run stopped:')} {t('Contact MedEffects to review this run.')}
            </Banner>
          )}
          {/* A run with no finish time never completed. Its claimed customers are in limbo - saying
              so is the difference between an operator investigating and an operator assuming. */}
          {data?.unfinished && !run?.abortedReason && (
            <Banner tone="warn">
              {t(
                'This run never finished. Some customers may have been claimed without being emailed.'
              )}
            </Banner>
          )}
          {run && run.mode !== 'live' && (
            <Banner tone="info">
              {t('This was a rehearsal. No customer was emailed, whatever the outcomes below say.')}
            </Banner>
          )}

          {data && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[12px] text-[#64748b] border-b border-[#e2e8f0]">
                    <th className="py-2 font-medium">{t('Customer')}</th>
                    <th className="py-2 font-medium">{t('Email')}</th>
                    <th className="py-2 font-medium text-right">{t('Balance')}</th>
                    <th className="py-2 font-medium">{t('Outcome')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.rows || []).length === 0 && (
                    <tr>
                      <td colSpan={4} className="py-6 text-center text-[#64748b]">
                        {t('This run reached no customers.')}
                      </td>
                    </tr>
                  )}
                  {(data.rows || []).map((row) => (
                    <tr
                      key={`${row.qbCustomerId}`}
                      className="border-b border-[#f1f5f9] last:border-0"
                    >
                      <td className="py-2.5 text-[#0f172a]">{row.customerName}</td>
                      <td className="py-2.5 text-[#64748b] break-all">{row.email || '--'}</td>
                      <td className="py-2.5 text-right tabular-nums">{money(row.totalOpen)}</td>
                      <td className="py-2.5">
                        <div className="text-[#475569]">
                          {t(statementOutcomeLabel(row.outcome))}
                        </div>
                        {row.error && (
                          <div className="text-[12px] text-[#f23e41]">
                            {t('This statement needs review. Contact MedEffects for help.')}
                          </div>
                        )}
                        {(row.refusals || []).length > 0 && (
                          <div className="text-[12px] text-[#005fb8]">
                            {row.refusals.map((code) => t(refusalLabel(code))).join(', ')}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {data?.truncated && (
            <p className="text-[12px] text-[#94a3b8] mt-2">
              {t('Showing the first 1,000 customers of this run.')}
            </p>
          )}
        </div>
      </>
    </Modal>
  );
}
